import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const CartContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const MAX_QTY = 10;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("naguara_cart")) || [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [unavailableIds, setUnavailableIds] = useState([]);
  const [shipping, setShipping] = useState({ configured: false, flat: null, free_from: null });

  // The catalogue is the only source of truth: a cart saved days ago is
  // re-synced (price, name, stock) and any coffee that no longer exists is
  // dropped, so the payment step can never receive stale data.
  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => {
        const catalog = r.data || [];
        setUnavailableIds(catalog.filter((p) => p.available === false).map((p) => p.id));
        setItems((prev) =>
          prev
            .map((i) => {
              const p = catalog.find((c) => c.id === i.product_id);
              if (!p) return null;
              return {
                ...i,
                name: p.name,
                price: p.price,
                image: p.image,
                sticker: p.sticker,
                origin: p.origin,
                available: p.available !== false,
                quantity: Math.min(MAX_QTY, Math.max(1, i.quantity)),
              };
            })
            .filter(Boolean)
        );
      })
      .catch(() => {});

    axios
      .get(`${API}/shipping`)
      .then((r) => setShipping(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem("naguara_cart", JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product) => {
    if (product?.available === false) return;      // never allow an unavailable coffee
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product_id === product.id
            ? { ...i, quantity: Math.min(MAX_QTY, i.quantity + 1) }
            : i
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          quantity: 1,
          name: product.name,
          price: product.price,
          image: product.image,
          sticker: product.sticker,
          origin: product.origin,
          available: product.available !== false,
        },
      ];
    });
  }, []);

  const updateQty = useCallback((productId, delta) => {
    setItems((prev) =>
      prev
        .map((i) =>
          i.product_id === productId
            ? { ...i, quantity: Math.min(MAX_QTY, i.quantity + delta) }
            : i
        )
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((productId) => {
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const count = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  const freeFrom = shipping.free_from;
  const shippingCost =
    !shipping.configured || items.length === 0
      ? 0
      : freeFrom != null && subtotal >= freeFrom
        ? 0
        : shipping.flat || 0;
  const missingForFree =
    shipping.configured && freeFrom != null && subtotal < freeFrom ? freeFrom - subtotal : 0;
  const total = subtotal + shippingCost;

  const isItemUnavailable = useCallback(
    (item) => item?.available === false || unavailableIds.includes(item?.product_id),
    [unavailableIds]
  );
  const hasUnavailable = items.some(isItemUnavailable);

  const removeUnavailable = useCallback(
    () => setItems((prev) => prev.filter((i) => !isItemUnavailable(i))),
    [isItemUnavailable]
  );

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        updateQty,
        removeItem,
        clearCart,
        count,
        subtotal,
        total,
        shipping,
        shippingCost,
        missingForFree,
        isOpen,
        setIsOpen,
        isItemUnavailable,
        hasUnavailable,
        removeUnavailable,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
